/**
 * §10 «Меню (категории)» (design-guest v7, route /menu, guest).
 *
 * Hero «Что Вам подать?» + лид «Доставка в номер от 20 минут», баннер
 * «Заказ за столом № N» если гость пришёл по QR стола (?table=N), tile-grid
 * категорий (иконка + название + часы/доступность). Tap по плитке → §11
 * (/menu/c/{code}). Sticky-CTA «Перейти к заказу · N · ₽» если в корзине
 * что-то есть → §12.
 *
 * Источник категорий — F&B-витрина каталога (useFnbCatalog), как и раньше.
 */
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Skeleton } from '@hsc/ui';
import { resolveGuestLocale } from '../../lib/locale.js';
import { useCart } from './useCart.js';
import { useFnbCatalog } from './useMenu.js';
import { categoryAvailability, categoryIcon } from './guest-shared.js';
import { GuestCartCta } from './GuestCartCta.js';

export function GuestMenuCategories(): JSX.Element {
  const locale = useMemo(() => resolveGuestLocale(), []);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tableNo = params.get('table') ?? undefined;
  const { data: categories, isLoading, isError, refetch } = useFnbCatalog(locale);
  const cart = useCart();

  if (isLoading) {
    return (
      <section aria-busy="true" data-testid="menu-skeleton">
        <h1 className="hsc-page__title">Что Вам подать?</h1>
        <div className="fnb-tiles">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="fnb-tile fnb-tile--skeleton" data-testid="menu-skeleton-tile">
              <Skeleton height={56} radius={12} />
              <div style={{ height: 8 }} />
              <Skeleton width={90} height={14} radius={6} />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (isError || categories === undefined) {
    return (
      <section data-testid="menu-error">
        <h1 className="hsc-page__title">Что Вам подать?</h1>
        <Card>
          <p role="alert">Не удалось загрузить меню. Попробуйте ещё раз.</p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => void refetch()}>
              Попробовать ещё раз
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid="menu-page">
      {tableNo !== undefined && tableNo !== '' && (
        <div className="fnb-table-banner" data-testid="menu-table-banner">
          Заказ за столом № {tableNo} — доставка не нужна, официант принесёт сюда.
        </div>
      )}

      <h1 className="hsc-page__title">Что Вам подать?</h1>
      <p className="ta-muted">Доставка в номер от 20 минут.</p>

      {categories.length === 0 ? (
        <Card data-testid="menu-empty">
          <p>Кухня сегодня закрыта. Загляните завтра с 7 утра.</p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => navigate('/services')}>
              К услугам отеля
            </Button>
          </div>
        </Card>
      ) : (
        <div className="fnb-tiles" data-testid="menu-tiles">
          {categories.map((category) => {
            const avail = categoryAvailability(category.code, category.name);
            return (
              <button
                type="button"
                key={category.id}
                className={`fnb-tile${avail.open ? '' : ' fnb-tile--closed'}`}
                data-testid={`menu-tile-${category.code}`}
                onClick={() => navigate(`/menu/c/${encodeURIComponent(category.code)}`)}
              >
                <span className="fnb-tile__head">
                  <span className="fnb-tile__icon" aria-hidden="true">
                    {categoryIcon(category.code, category.name)}
                  </span>
                  <span className="fnb-tile__name">{category.name}</span>
                </span>
                <span className="fnb-tile__sub">{avail.hours}</span>
              </button>
            );
          })}
        </div>
      )}

      <GuestCartCta locale={locale} cart={cart} />
    </section>
  );
}
